import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  createPulse,
  deletePulse,
  getPulse,
  getPulsePostData,
  getPulseSnapshot,
  updatePulse,
} from "./pulse.api";
import type {
  CreatePulseProps,
  PulseData,
  PulseWithPostData,
} from "../types/pulse.types";
import { usePulseApiToken } from "@/features/auth/hooks/useApiToken";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { extractErrorMessage } from "@/lib/errors";
import { useQueryErrorToast } from "@/hooks/useQueryErrorToast";

export const useGetPulses = () => {
  const { apiToken } = usePulseApiToken();

  const query = useQuery({
    queryKey: ["pulses"],
    queryFn: () => getPulse(apiToken as string),
    enabled: !!apiToken,
    retry: 2,
  });

  useQueryErrorToast(query, "Failed to load Pulses");

  return query;
};

export const useGetPulsesWithPostData = () => {
  const { data: pulsesData, ...pulsesQuery } = useGetPulses();
  const { apiToken } = usePulseApiToken();
  const errorShownForPulse = useRef<Set<string>>(new Set());

  const pulses: PulseData[] = pulsesData?.data || [];

  const postDataQueries = useQueries({
    queries: pulses.map((pulse) => ({
      queryKey: ["pulse-post-data", pulse.post_url],
      queryFn: () => getPulsePostData(pulse.post_url, apiToken as string),
      enabled: !!apiToken && !!pulse.post_url,
      retry: 2,
    })),
  });

  useEffect(() => {
    postDataQueries.forEach((query, index) => {
      const pulse = pulses[index];
      if (
        query.isError &&
        query.error &&
        pulse &&
        !errorShownForPulse.current.has(pulse._id)
      ) {
        toast.error("Failed to load Pulse Post Data", {
          description: extractErrorMessage(query.error),
        });
        errorShownForPulse.current.add(pulse._id);
      } else if (!query.isError && pulse) {
        errorShownForPulse.current.delete(pulse._id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postDataQueries]);

  const pulsesWithPostData: PulseWithPostData[] = pulses.map(
    (pulse, index) => ({
      ...pulse,
      postData: postDataQueries[index]?.data || null,
      postDataLoading: postDataQueries[index]?.isLoading || false,
      postDataError: postDataQueries[index]?.error || null,
    }),
  );

  const isAnyPostDataLoading =
    pulses.length > 0 && postDataQueries.some((query) => query.isLoading);

  const hasErrors = postDataQueries.some((query) => query.isError);

  return {
    ...pulsesQuery,
    isLoading: pulsesQuery.isLoading || isAnyPostDataLoading,
    isError: pulsesQuery.isError || hasErrors,
    data: {
      ...pulsesData,
      data: pulsesWithPostData,
    },
    pulsesData, // Expose raw pulses data for consumers to avoid duplicate useGetPulses calls
    postDataQueries,
  };
};

export const useGetPulsePostData = (
  postUrl: string,
  enabled: boolean = true,
) => {
  const { apiToken } = usePulseApiToken();

  const query = useQuery({
    queryKey: ["pulse-post-data", postUrl],
    queryFn: () => getPulsePostData(postUrl, apiToken as string),
    enabled: !!apiToken && !!postUrl && enabled,
    retry: 2,
  });

  useQueryErrorToast(query, "Failed to load Pulse Post Data");

  return query;
};

export const useGetPulseSnapshot = (
  pulseId: string,
  enabled: boolean = true,
) => {
  const { apiToken } = usePulseApiToken();

  const query = useQuery({
    queryKey: ["pulse-snapshot", pulseId],
    queryFn: () => getPulseSnapshot(pulseId, apiToken as string),
    enabled: !!apiToken && !!pulseId && enabled,
    retry: 1,
  });

  useQueryErrorToast(query, "Failed to load Pulse Snapshot");

  return query;
};

export const useRefreshPulseSnapshot = () => {
  const queryClient = useQueryClient();
  const { apiToken } = usePulseApiToken();

  return useMutation({
    mutationFn: ({ pulseId }: { pulseId: string; postUrl: string }) =>
      getPulseSnapshot(pulseId, apiToken as string),
    onSuccess: (data, { pulseId, postUrl }) => {
      queryClient.setQueryData(["pulse-snapshot", pulseId], data);
      queryClient.invalidateQueries({ queryKey: ["pulse-post-data", postUrl] });
      if (data.has_changes) {
        toast.success("Snapshot refreshed - Changes detected!");
      } else {
        toast.info("Snapshot refreshed - No changes detected");
      }
    },
    onError: (error) => {
      toast.error("Failed to refresh Pulse Snapshot", {
        description: extractErrorMessage(error),
      });
    },
  });
};

export const useCreatePulse = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { apiToken } = usePulseApiToken();
  return useMutation({
    mutationFn: (payload: CreatePulseProps) =>
      createPulse(payload, apiToken as string),
    onSuccess: async () => {
      toast.success("Pulse created successfully");
      await queryClient.refetchQueries({ queryKey: ["pulses"] });
      await queryClient.invalidateQueries({ queryKey: ["pulse-post-data"] });
      navigate({ to: "/pulse", search: {} });
    },
    onError: (error) => {
      toast.error("Failed to create Pulse", {
        description: extractErrorMessage(error),
      });
    },
  });
};

export const useDeletePulse = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { apiToken } = usePulseApiToken();
  return useMutation({
    mutationFn: (pulseId: string) => deletePulse(pulseId, apiToken as string),
    onSuccess: async () => {
      toast.success("Pulse deleted successfully");
      await queryClient.refetchQueries({ queryKey: ["pulses"] });
      await queryClient.invalidateQueries({ queryKey: ["pulse-post-data"] });
      await queryClient.invalidateQueries({ queryKey: ["pulse-snapshot"] });
      navigate({ to: "/pulse", search: {} });
    },
    onError: (error) => {
      toast.error("Failed to delete Pulse", {
        description: extractErrorMessage(error),
      });
    },
  });
};

export const useUpdatePulse = () => {
  const queryClient = useQueryClient();
  const { apiToken } = usePulseApiToken();
  return useMutation({
    mutationFn: (pulseId: string) => updatePulse(pulseId, apiToken as string),
    onSuccess: async () => {
      toast.success("Pulse updated successfully");
      await queryClient.refetchQueries({ queryKey: ["pulses"] });
      await queryClient.invalidateQueries({ queryKey: ["pulse-post-data"] });
      await queryClient.invalidateQueries({ queryKey: ["pulse-snapshot"] });
    },
    onError: (error) => {
      toast.error("Failed to update Pulse", {
        description: extractErrorMessage(error),
      });
    },
  });
};
